import { Instant, PlainDate } from "@/temporal"
import { arraySchema, objectSchema } from "vue-utils"
import { extend, Identifiable, Nullable } from "../BaseEntity"
import type { ObjectSchemaProperty } from "../ObjectSchemaProperty"
import type { Sort, SortDirection } from "../Sort"
import { MarriageAddressSchema, type MarriageAddress } from "./MarriageAddress"
import { MarriageIncomeBand, MarriageIncomeBandSchema } from "./MarriageIncomeBand"
import { MarriagePerson, MarriagePersonSchema } from "./MarriagePerson"
import { MarriagePersonDetails } from "./MarriagePersonDetails"
import { MarriageSelfAssessed } from "./MarriageSelfAssessed"
import { MarriageSignature } from "./MarriageSignature"
import { MarriageStatus, MarriageStatusSchema } from "./MarriageStatus"
import { MarriageTaxYearSchema, type MarriageTaxYear } from "./MarriageTaxYear"
import { MarriageType, MarriageTypeSchema } from "./MarriageType"
import { MarriageValidity, MarriageValiditySchema } from "./MarriageValidity"

export interface MarriageClaim extends Identifiable {
	ClaimRef: string
	ClaimReference: string
	ClaimId: Nullable<number>
	Status: MarriageStatus
	Type: MarriageType
	Version: number
	ClaimedBefore: Nullable<boolean>
	ClaimedBeforeTaxYears: number[]
	HighestEarner: Nullable<MarriagePerson>
	IncomeBand: Nullable<MarriageIncomeBand>
	PartnerIncomeBand: Nullable<MarriageIncomeBand>
	IsSelfAssessed: Nullable<boolean>
	SelfAssessed: MarriageSelfAssessed
	TaxYears: MarriageTaxYear[]
	Details: MarriagePersonDetails
	PartnerDetails: MarriagePersonDetails
	MarriageDate: Nullable<PlainDate>
	Address: MarriageAddress
	Signature: MarriageSignature
	PartnerSignature: MarriageSignature
	OptedInToUniformBenefits: Nullable<boolean>
	UniformBenefitsSignUpCompleted: boolean
	FailureReason: Nullable<string>
	Validity: MarriageValidity
	DisabledSteps: number[]
	ResumeUrl: Nullable<string>
	PartnerReturnUrl: Nullable<string>
	PartnerQRUrl: Nullable<string>
	PartnerQRCodeB64: Nullable<string>
	BrandUrl: string
	P1ExportEligible: boolean
	P2ExportEligible: boolean
	DateExported: Nullable<Instant>
	DateCreated: Instant
	DateUpdated: Instant
}

export const MarriageClaimSchema = objectSchema<MarriageClaim>({
	...extend(Identifiable),
	ClaimRef: String,
	ClaimReference: String,
	ClaimId: Nullable(Number),
	Status: MarriageStatusSchema,
	Type: MarriageTypeSchema,
	Version: Number,
	ClaimedBefore: Nullable(Boolean),
	ClaimedBeforeTaxYears: arraySchema(Number),
	HighestEarner: Nullable(MarriagePersonSchema),
	IncomeBand: Nullable(MarriageIncomeBandSchema),
	PartnerIncomeBand: Nullable(MarriageIncomeBandSchema),
	IsSelfAssessed: Nullable(Boolean),
	SelfAssessed: MarriageSelfAssessed,
	TaxYears: arraySchema(MarriageTaxYearSchema),
	Details: MarriagePersonDetails,
	PartnerDetails: MarriagePersonDetails,
	MarriageDate: Nullable(PlainDate.Schema),
	Address: MarriageAddressSchema,
	Signature: MarriageSignature,
	PartnerSignature: MarriageSignature,
	OptedInToUniformBenefits: Nullable(Boolean),
	UniformBenefitsSignUpCompleted: Boolean,
	FailureReason: Nullable(String),
	Validity: MarriageValiditySchema,
	DisabledSteps: arraySchema(Number),
	ResumeUrl: Nullable(String),
	PartnerReturnUrl: Nullable(String),
	PartnerQRUrl: Nullable(String),
	PartnerQRCodeB64: Nullable(String),
	BrandUrl: String,
	P1ExportEligible: Boolean,
	P2ExportEligible: Boolean,
	DateExported: Nullable(Instant.Schema),
	DateCreated: Instant.Schema,
	DateUpdated: Instant.Schema,
})

export type MarriageClaimSort = Sort<ObjectSchemaProperty>

export interface SendableMarriageClaimSort {
	direction: SortDirection
	by: string[]
}
